import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  FiX, 
  FiCalendar, 
  FiMapPin, 
  FiUsers, 
  FiSearch,
  FiUser,
  FiUserCheck,
  FiBook,
  FiSave,
  FiInfo,
  FiChevronRight,
  FiChevronLeft,
  FiCheck
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { protonCalendarAPI } from '../../../services/calendarAPI';
import './CreateEventModal.scss';

const EVENT_TYPES = [
  { value: 'meeting', label: 'Meeting', icon: '👥', color: '#007AFF', description: 'Meetings, conferences, discussions' },
  { value: 'event', label: 'Event', icon: '🎉', color: '#5856D6', description: 'General events and announcements' },
  { value: 'training', label: 'Training', icon: '📚', color: '#AF52DE', description: 'Training sessions and workshops' },
  { value: 'workshop', label: 'Workshop', icon: '🔧', color: '#FF2D92', description: 'Hands-on learning sessions' },
  { value: 'orientation', label: 'Orientation', icon: '🎯', color: '#FF3B30', description: 'New member orientations' },
  { value: 'sports', label: 'Sports', icon: '⚽', color: '#FF9500', description: 'Sports events and competitions' },
  { value: 'competition', label: 'Competition', icon: '🏆', color: '#FFCC00', description: 'Academic and other competitions' },
  { value: 'rehearsal', label: 'Rehearsal', icon: '🎭', color: '#34C759', description: 'Practice sessions and rehearsals' },
  { value: 'deadline', label: 'Deadline', icon: '⏰', color: '#FF6347', description: 'Important deadlines and due dates' },
  { value: 'assessment', label: 'Assessment', icon: '📝', color: '#32D74B', description: 'Tests, quizzes, and evaluations' },
  { value: 'registration', label: 'Registration', icon: '📋', color: '#5AC8FA', description: 'Registration periods and forms' }
];

const ASSIGNMENT_TYPES = [
  { value: 'all', label: 'Everyone', icon: '🌐', description: 'All users in the institute' },
  { value: 'students_all', label: 'All Students', icon: '🎓', description: 'All students in the institute' },
  { value: 'teachers_all', label: 'All Teachers', icon: '👨‍🏫', description: 'All teachers in the institute' },
  { value: 'role', label: 'Specific Roles', icon: '👥', description: 'Users with specific roles (multiple selection)' },
  { value: 'class', label: 'Specific Classes', icon: '📚', description: 'Students in specific classes (multiple selection)' },
  { value: 'section', label: 'Specific Sections', icon: '📑', description: 'Students in specific sections (multiple selection)' },
  { value: 'individual', label: 'Specific People', icon: '👤', description: 'Selected individuals (multiple selection)' }
];

// Stepper configuration
const STEPS = [
  { 
    id: 'event-type', 
    title: 'Event Type', 
    subtitle: 'Choose the type of event you want to create',
    icon: '📅'
  },
  { 
    id: 'event-details', 
    title: 'Event Details', 
    subtitle: 'Add title, description, date, time and location',
    icon: '✏️'
  },
  { 
    id: 'assignment', 
    title: 'Assignment', 
    subtitle: 'Choose who can see this event',
    icon: '👥'
  }
];

const CreateEventModal = ({ 
  isOpen, 
  onClose, 
  selectedDate, 
  clickPosition = null,
  arrowDirection = 'top',
  editingEvent = null,
  isEditMode = false
}) => {
  const { institute, current_academic_id, academic_years } = useSelector(state => state.auth);
  
  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_datetime: '',
    end_datetime: '',
    event_type: '',
    assignment_type: 'all',
    role_ids: [],
    class_ids: [],
    section_ids: [],
    user_ids: [],
    location: '',
    color: '#5856D6',
    is_all_day: false
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [assignmentOptions, setAssignmentOptions] = useState({ 
    classes: [], 
    users: [], 
    roles: [], 
    sections: [] 
  });
  const [assignmentCache, setAssignmentCache] = useState({
    classes: null,
    roles: null,
    sections: null
  }); // Cache for non-user data
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAssignmentSearch, setShowAssignmentSearch] = useState(false);
  const [selectedAcademicId, setSelectedAcademicId] = useState(current_academic_id);
  const [academicWarning, setAcademicWarning] = useState(null);
  
  // Refs
  const modalRef = useRef(null);
  const searchRef = useRef(null);

  // Calculate modal position
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [modalArrowDirection, setModalArrowDirection] = useState(arrowDirection);

  const calculateModalPosition = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const modalWidth = Math.min(700, viewportWidth - 40); // Max 700px but ensure 20px margin
    const modalHeight = Math.min(600, viewportHeight - 40); // Max 600px but ensure 20px margin
    
    // Always center the modal - ignore click position for better UX
    const x = Math.max(20, (viewportWidth - modalWidth) / 2);
    const y = Math.max(20, (viewportHeight - modalHeight) / 2);
    
    setModalPosition({ x, y });
    setModalArrowDirection('none'); // No arrow for centered modal
  }, []);

  const checkAcademicYear = useCallback((date) => {
    const selectedYear = academic_years.find(year => {
      const startDate = new Date(year.start_date);
      const endDate = new Date(year.end_date);
      const checkDate = new Date(date);
      return checkDate >= startDate && checkDate <= endDate;
    });

    // Always use the active academic year from the UI — never auto-switch based on the event date.
    // selectedAcademicId stays as current_academic_id; we only show a warning if the date
    // falls outside the current academic year range.
    if (selectedYear && selectedYear.academic_id !== current_academic_id) {
      setAcademicWarning({
        message: `This date falls in academic year ${selectedYear.year_label}, but the event will be created under the currently active academic year.`,
        academicYear: selectedYear
      });
    } else if (!selectedYear) {
      setAcademicWarning({
        message: 'This date does not fall within any defined academic year.',
        academicYear: null
      });
    } else {
      setAcademicWarning(null);
    }
  }, [academic_years, current_academic_id]);

  // Load specific assignment options with caching
  const loadClassOptions = useCallback(async () => {
    if (assignmentCache.classes) {
      setAssignmentOptions(prev => ({ ...prev, classes: assignmentCache.classes }));
      return;
    }

    try {
      const params = {
        institute_id: institute.institute_id,
        academic_id: selectedAcademicId
      };
      
      const response = await protonCalendarAPI.getClasses(params);
      if (response.success) {
        setAssignmentOptions(prev => ({ ...prev, classes: response.data }));
        setAssignmentCache(prev => ({ ...prev, classes: response.data }));
      }
    } catch (error) {
      console.error('Failed to load class options:', error);
    }
  }, [institute.institute_id, selectedAcademicId, assignmentCache.classes]);

  const loadRoleOptions = useCallback(async () => {
    if (assignmentCache.roles) {
      setAssignmentOptions(prev => ({ ...prev, roles: assignmentCache.roles }));
      return;
    }

    try {
      const response = await protonCalendarAPI.getRoles();
      if (response.success) {
        setAssignmentOptions(prev => ({ ...prev, roles: response.data }));
        setAssignmentCache(prev => ({ ...prev, roles: response.data }));
      }
    } catch (error) {
      console.error('Failed to load role options:', error);
    }
  }, [assignmentCache.roles]);

  const loadSectionOptions = useCallback(async (classId = null) => {
    const cacheKey = `sections_${classId || 'all'}`;
    if (assignmentCache[cacheKey]) {
      setAssignmentOptions(prev => ({ ...prev, sections: assignmentCache[cacheKey] }));
      return;
    }

    try {
      const params = {
        institute_id: institute.institute_id,
        academic_id: selectedAcademicId,
        class_id: classId
      };
      
      const response = await protonCalendarAPI.getSections(params);
      if (response.success) {
        setAssignmentOptions(prev => ({ ...prev, sections: response.data }));
        setAssignmentCache(prev => ({ ...prev, [cacheKey]: response.data }));
      }
    } catch (error) {
      console.error('Failed to load section options:', error);
    }
  }, [institute.institute_id, selectedAcademicId, assignmentCache]);

  const loadUserOptions = useCallback(async (searchTerm = '', roleFilter = 'all') => {
    // Always fetch users as they are not cached due to large count
    try {
      const params = {
        institute_id: institute.institute_id,
        academic_id: selectedAcademicId,
        search: searchTerm,
        role_filter: roleFilter,
        limit: 50
      };
      
      const response = await protonCalendarAPI.getUsers(params);
      if (response.success) {
        setAssignmentOptions(prev => ({ ...prev, users: response.data }));
      }
    } catch (error) {
      console.error('Failed to load user options:', error);
    }
  }, [institute.institute_id, selectedAcademicId]);

  useEffect(() => {
    if (isOpen) {
      calculateModalPosition();
      
      // Recalculate position on window resize
      const handleResize = () => {
        calculateModalPosition();
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, calculateModalPosition]);

  useEffect(() => {
    if (isOpen) {
      // Reset form and stepper when modal opens
      setCurrentStep(0);
      setErrors({});
      const defaultDate = selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const defaultDateTime = selectedDate ? selectedDate.toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16);
      const defaultEndDateTime = selectedDate ? selectedDate.toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16);
      
      setFormData({
        title: '',
        description: '',
        start_datetime: defaultDateTime,
        end_datetime: defaultEndDateTime,
        event_type: '',
        assignment_type: 'all',
        role_ids: [],
        class_ids: [],
        section_ids: [],
        user_ids: [],
        location: '',
        color: '#5856D6',
        is_all_day: false
      });
      
      setSelectedAcademicId(current_academic_id);
      checkAcademicYear(defaultDate);
      // Don't load assignment options on modal open - only when specific type is selected
    }
  }, [isOpen, selectedDate, current_academic_id, checkAcademicYear]);

  // Effect to populate form when in edit mode
  useEffect(() => {
    if (isOpen && isEditMode && editingEvent) {
      // Parse the datetime values
      const startDate = new Date(editingEvent.start);
      const endDate = editingEvent.end ? new Date(editingEvent.end) : startDate;
      
      // Format datetime for input fields
      const startDateTime = startDate.toISOString().slice(0, 16);
      const endDateTime = endDate.toISOString().slice(0, 16);
      
      // Populate form with event data
      setFormData({
        title: editingEvent.title || '',
        description: editingEvent.description || '',
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        event_type: editingEvent.event_type || '',
        assignment_type: editingEvent.assignment_type || 'all',
        role_ids: editingEvent.assignment_details?.roles?.map(r => r.id) || [],
        class_ids: editingEvent.assignment_details?.classes?.map(c => c.id) || [],
        section_ids: editingEvent.assignment_details?.sections?.map(s => s.id) || [],
        user_ids: editingEvent.assignment_details?.users?.map(u => u.id) || [],
        location: editingEvent.location || '',
        color: editingEvent.backgroundColor || '#5856D6',
        is_all_day: editingEvent.allDay || false
      });
      
      // Skip to event details step for edit mode (type is already selected)
      if (editingEvent.event_type) {
        setCurrentStep(1);
      }
      
      // Check academic year
      checkAcademicYear(startDate.toISOString().split('T')[0]);
    }
  }, [isOpen, isEditMode, editingEvent, checkAcademicYear]);

  // Separate effect to populate assignment options when editing
  useEffect(() => {
    if (isOpen && isEditMode && editingEvent?.assignment_details) {
      const newAssignmentOptions = { classes: [], users: [], roles: [], sections: [] };
      
      if (editingEvent.assignment_details.users) {
        newAssignmentOptions.users = editingEvent.assignment_details.users.map(user => ({
          id: user.id,
          user_id: user.id,
          first_name: user.name.split(' ')[0] || user.name,
          last_name: user.name.split(' ').slice(1).join(' ') || '',
          role_id: user.role,
          role_name: user.role === 1 ? 'Admin' : user.role === 3 ? 'Student' : user.role === 5 ? 'Employee' : 'Unknown'
        }));
      }
      
      if (editingEvent.assignment_details.roles) {
        newAssignmentOptions.roles = editingEvent.assignment_details.roles.map(role => ({
          id: role.id,
          role_id: role.id,
          role_name: role.name,
          label: role.name
        }));
      }
      
      if (editingEvent.assignment_details.classes) {
        newAssignmentOptions.classes = editingEvent.assignment_details.classes.map(cls => ({
          id: cls.id,
          class_id: cls.id,
          class_name: cls.name,
          class_code: cls.code || cls.name,
          label: cls.name
        }));
      }
      
      if (editingEvent.assignment_details.sections) {
        newAssignmentOptions.sections = editingEvent.assignment_details.sections.map(section => ({
          id: section.id,
          section_id: section.id,
          section_name: section.name,
          class_name: section.class_name || '',
          label: section.name
        }));
      }
      
      setAssignmentOptions(newAssignmentOptions);
    }
  }, [isOpen, isEditMode, editingEvent]);

  useEffect(() => {
    // Update color when event type changes
    const selectedType = EVENT_TYPES.find(type => type.value === formData.event_type);
    if (selectedType && formData.color !== selectedType.color) {
      setFormData(prev => ({ ...prev, color: selectedType.color }));
    }
  }, [formData.event_type, formData.color]);

  useEffect(() => {
    // Check academic year when date changes
    if (formData.start_date) {
      checkAcademicYear(formData.start_date);
    }
  }, [formData.start_date, checkAcademicYear]);

  // Local search function for cached data
  const performLocalSearch = useCallback((term) => {
    const searchLower = term.toLowerCase().trim();
    
    if (formData.assignment_type === 'class' && assignmentCache.classes) {
      const filteredClasses = assignmentCache.classes.filter(cls => 
        cls.label.toLowerCase().includes(searchLower) ||
        cls.name?.toLowerCase().includes(searchLower)
      );
      setAssignmentOptions(prev => ({ ...prev, classes: filteredClasses }));
    } else if (formData.assignment_type === 'role' && assignmentCache.roles) {
      const filteredRoles = assignmentCache.roles.filter(role => 
        role.name.toLowerCase().includes(searchLower) ||
        role.label?.toLowerCase().includes(searchLower)
      );
      setAssignmentOptions(prev => ({ ...prev, roles: filteredRoles }));
    } else if (formData.assignment_type === 'section') {
      // Look for sections cache with any key pattern
      const sectionsCacheKey = Object.keys(assignmentCache).find(key => key.startsWith('sections_'));
      const sectionsData = sectionsCacheKey ? assignmentCache[sectionsCacheKey] : null;
      
      if (sectionsData) {
        const filteredSections = sectionsData.filter(section => 
          section.label.toLowerCase().includes(searchLower) ||
          section.name?.toLowerCase().includes(searchLower)
        );
        setAssignmentOptions(prev => ({ ...prev, sections: filteredSections }));
      }
    }
  }, [formData.assignment_type, assignmentCache]);

  // Reset to full cached data
  const resetToFullData = useCallback(() => {
    if (formData.assignment_type === 'class' && assignmentCache.classes) {
      setAssignmentOptions(prev => ({ ...prev, classes: assignmentCache.classes }));
    } else if (formData.assignment_type === 'role' && assignmentCache.roles) {
      setAssignmentOptions(prev => ({ ...prev, roles: assignmentCache.roles }));
    } else if (formData.assignment_type === 'section') {
      // Look for sections cache with any key pattern
      const sectionsCacheKey = Object.keys(assignmentCache).find(key => key.startsWith('sections_'));
      const sectionsData = sectionsCacheKey ? assignmentCache[sectionsCacheKey] : null;
      
      if (sectionsData) {
        setAssignmentOptions(prev => ({ ...prev, sections: sectionsData }));
      }
    } else if (formData.assignment_type === 'individual') {
      // For users, reload without search term
      loadUserOptions('', roleFilter);
    }
  }, [formData.assignment_type, assignmentCache, roleFilter, loadUserOptions]);

  useEffect(() => {
    // Handle search term changes for all assignment types
    if (showAssignmentSearch && searchTerm.length > 0) {
      if (formData.assignment_type === 'individual') {
        // Debounced search for users (not cached due to large count)
        const debounceTimer = setTimeout(() => {
          loadUserOptions(searchTerm, roleFilter);
        }, 300);
        return () => clearTimeout(debounceTimer);
      } else {
        // Local search for cached data (classes, roles, sections)
        performLocalSearch(searchTerm);
      }
    } else if (showAssignmentSearch && searchTerm.length === 0) {
      // Reset to full cached data when search is cleared
      resetToFullData();
    }
  }, [searchTerm, roleFilter, selectedAcademicId, showAssignmentSearch, formData.assignment_type, loadUserOptions, performLocalSearch, resetToFullData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updatedData = { ...prev, [field]: value };
      
      // Auto-set end_datetime when start_datetime changes
      if (field === 'start_datetime' && value) {
        // If end_datetime is empty or if we're changing the date part, update end_datetime to match the start date
        if (!prev.end_datetime || prev.end_datetime.split('T')[0] !== value.split('T')[0]) {
          const startDate = value.split('T')[0];
          const startTime = value.split('T')[1] || '00:00';
          
          // Set end datetime to same day, but add 1 hour if it's not an all-day event
          if (prev.is_all_day) {
            updatedData.end_datetime = `${startDate}T23:59`;
          } else {
            // Add 1 hour to start time for default end time
            const [hours, minutes] = startTime.split(':').map(Number);
            const endHours = Math.min(hours + 1, 23);
            const endTime = `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            updatedData.end_datetime = `${startDate}T${endTime}`;
          }
        }
      }
      
      return updatedData;
    });
    
    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAssignmentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      assignment_type: type,
      class_ids: [],
      section_ids: [],
      user_ids: [],
      role_ids: []
    }));
    
    const needsSearch = ['class', 'section', 'individual', 'role'].includes(type);
    setShowAssignmentSearch(needsSearch);
    
    // Reset search term when opening assignment overlay
    if (needsSearch) {
      setSearchTerm('');
      setRoleFilter('all');
      // Focus search input after overlay opens
      setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
        }
      }, 100);
    }
    
    // Load specific options based on assignment type when search overlay opens
    if (needsSearch) {
      switch (type) {
        case 'class':
          loadClassOptions();
          break;
        case 'role':
          loadRoleOptions();
          break;
        case 'section':
          loadSectionOptions();
          break;
        case 'individual':
          // Load initial users (without search) - this will be called again with search
          loadUserOptions('', roleFilter);
          break;
        default:
          break;
      }
    }
  };

  const handleClassSelect = (classId) => {
    setFormData(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
    
    // If we're in section assignment mode, load sections for all selected classes
    if (formData.assignment_type === 'section') {
      // For simplicity, load all sections when any class is selected
      loadSectionOptions();
    }
  };

  const handleRoleSelect = (roleId) => {
    setFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  // Stepper navigation functions
  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      if (validateCurrentStep()) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < STEPS.length) {
      setCurrentStep(stepIndex);
    }
  };

  // Validate current step
  const validateCurrentStep = () => {
    const step = STEPS[currentStep];
    const newErrors = {};

    switch (step.id) {
      case 'event-type':
        if (!formData.event_type) {
          newErrors.event_type = 'Please select an event type';
          setErrors(newErrors);
          return false;
        }
        break;
      
      case 'event-details':
        if (!formData.title.trim()) {
          newErrors.title = 'Title is required';
        }
        if (!formData.start_datetime) {
          newErrors.start_datetime = 'Start date and time is required';
        }
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          return false;
        }
        break;
      
      case 'assignment':
        // Assignment validation can be done during submission
        break;
      
      default:
        break;
    }

    setErrors({});
    return true;
  };

  // Check if current step is valid (without setting errors)
  const isCurrentStepValid = () => {
    const step = STEPS[currentStep];

    switch (step.id) {
      case 'event-type':
        return !!formData.event_type;
      
      case 'event-details':
        return !!(formData.title.trim() && formData.start_datetime);
      
      case 'assignment':
        return true; // Assignment validation during submission
      
      default:
        return true;
    }
  };

  // Check if a specific step is valid
  const isStepValid = (stepIndex) => {
    const step = STEPS[stepIndex];

    switch (step.id) {
      case 'event-type':
        return !!formData.event_type;
      
      case 'event-details':
        return !!(formData.title.trim() && formData.start_datetime);
      
      case 'assignment':
        if (formData.assignment_type === 'all' || formData.assignment_type === 'students_all' || formData.assignment_type === 'teachers_all') {
          return true;
        }
        if (formData.assignment_type === 'class') return formData.class_ids.length > 0;
        if (formData.assignment_type === 'role') return formData.role_ids.length > 0;
        if (formData.assignment_type === 'section') return formData.section_ids.length > 0;
        if (formData.assignment_type === 'individual') return formData.user_ids.length > 0;
        return true;
      
      default:
        return true;
    }
  };

  // Get selection count for assignment types
  const getSelectedCount = (assignmentType) => {
    switch (assignmentType) {
      case 'role':
        return formData.role_ids.length;
      case 'class':
        return formData.class_ids.length;
      case 'section':
        return formData.section_ids.length;
      case 'individual':
        return formData.user_ids.length;
      default:
        return 0;
    }
  };

  // Handle event type selection
  const handleEventTypeSelect = (eventType) => {
    const selectedType = EVENT_TYPES.find(type => type.value === eventType);
    setFormData(prev => ({
      ...prev,
      event_type: eventType,
      color: selectedType?.color || '#5856D6'
    }));
    
    // Clear any existing errors for event_type
    setErrors(prev => ({ ...prev, event_type: null }));
    
    // Auto-advance to next step - directly move to next step since we know it's valid
    setTimeout(() => {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }, 300);
  };

  // Render assignment step content
  const renderAssignmentStep = () => {
    const selectedAssignmentType = ASSIGNMENT_TYPES.find(type => type.value === formData.assignment_type);
    
    return (
      <div className="assignment-section">
        <div className="section-title">
          <FiUsers size={18} />
          Who can see this event?
        </div>

        {/* Assignment Type Selection */}
        <div className="assignment-type-list">
          {ASSIGNMENT_TYPES.map(type => {
            const selectedCount = getSelectedCount(type.value);
            const hasSelections = selectedCount > 0;
            
            return (
              <button
                key={type.value}
                type="button"
                className={`assignment-option ${formData.assignment_type === type.value ? 'selected' : ''}`}
                onClick={() => handleAssignmentTypeChange(type.value)}
              >
                <div className="option-header">
                  <span className="option-icon">{type.icon}</span>
                  <span className="option-label">{type.label}</span>
                  {hasSelections && (
                    <span className="selection-badge">{selectedCount}</span>
                  )}
                </div>
                <p className="option-description">{type.description}</p>
              </button>
            );
          })}
        </div>

        {/* Full Screen Assignment Selection Overlay */}
        {showAssignmentSearch && (
          <div className="assignment-selection-overlay">
            <div className="overlay-content">
              {/* Search Header */}
              <div className="search-header">
                <div className="search-input-wrapper">
                  <FiSearch size={16} />
                  <input
                    ref={searchRef}
                    type="text"
                    className="search-input"
                    placeholder={`Search ${selectedAssignmentType?.label.toLowerCase() || formData.assignment_type}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        if (searchTerm) {
                          setSearchTerm('');
                        } else {
                          setShowAssignmentSearch(false);
                        }
                      }
                    }}
                    autoComplete="off"
                  />
                  {searchTerm && (
                    <button 
                      type="button"
                      className="clear-search-btn"
                      onClick={() => setSearchTerm('')}
                      title="Clear search"
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
                
                {formData.assignment_type === 'individual' && assignmentOptions.roles?.length > 0 && (
                  <div className="role-filter">
                    <select 
                      value={roleFilter} 
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="role-select"
                    >
                      <option value="all">All Roles</option>
                      {assignmentOptions.roles.map(role => (
                        <option key={role.id} value={role.name.toLowerCase()}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Search Results */}
              <div className="assignment-search">
                <div className="search-results">
                {formData.assignment_type === 'role' && (
                  <div className="role-list">
                    {assignmentOptions.roles.map(role => (
                      <button
                        key={role.id}
                        type="button"
                        className={`role-option compact ${formData.role_ids.includes(role.id) ? 'selected' : ''}`}
                        onClick={() => handleRoleSelect(role.id)}
                      >
                        <FiUsers size={16} />
                        <span>{role.name}</span>
                        {formData.role_ids.includes(role.id) && <FiCheck size={16} className="check-icon" />}
                      </button>
                    ))}
                    {assignmentOptions.roles.length === 0 && searchTerm && (
                      <p className="no-results">No roles found matching "{searchTerm}"</p>
                    )}
                    {assignmentOptions.roles.length === 0 && !searchTerm && (
                      <p className="no-results">No roles available</p>
                    )}
                  </div>
                )}

                {formData.assignment_type === 'class' && (
                  <div className="class-list">
                    {assignmentOptions.classes.map(cls => (
                      <button
                        key={cls.id}
                        type="button"
                        className={`class-option compact ${formData.class_ids.includes(cls.id) ? 'selected' : ''}`}
                        onClick={() => handleClassSelect(cls.id)}
                      >
                        <FiBook size={16} />
                        <span>{cls.label}</span>
                        {formData.class_ids.includes(cls.id) && <FiCheck size={16} className="check-icon" />}
                      </button>
                    ))}
                    {assignmentOptions.classes.length === 0 && searchTerm && (
                      <p className="no-results">No classes found matching "{searchTerm}"</p>
                    )}
                    {assignmentOptions.classes.length === 0 && !searchTerm && (
                      <p className="no-results">No classes available</p>
                    )}
                  </div>
                )}

                {formData.assignment_type === 'section' && (
                  <div className="section-list">
                    {assignmentOptions.sections.map(section => (
                      <button
                        key={section.id}
                        type="button"
                        className={`section-option compact ${formData.section_ids?.includes(section.id) ? 'selected' : ''}`}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            section_ids: prev.section_ids?.includes(section.id)
                              ? prev.section_ids.filter(id => id !== section.id)
                              : [...(prev.section_ids || []), section.id]
                          }));
                        }}
                      >
                        <FiBook size={16} />
                        <span>{section.label}</span>
                        {formData.section_ids?.includes(section.id) && <FiCheck size={16} className="check-icon" />}
                      </button>
                    ))}
                    {assignmentOptions.sections.length === 0 && searchTerm && (
                      <p className="no-results">No sections found matching "{searchTerm}"</p>
                    )}
                    {assignmentOptions.sections.length === 0 && !searchTerm && (
                      <p className="no-results">No sections available</p>
                    )}
                  </div>
                )}

                {formData.assignment_type === 'individual' && (
                  <div className="user-list">
                    {assignmentOptions.users.map(user => (
                      <button
                        key={user.id}
                        type="button"
                        className={`user-option compact ${formData.user_ids.includes(user.id) ? 'selected' : ''}`}
                        onClick={() => handleUserToggle(user.id)}
                      >
                        <div className="user-icon">
                          {formData.user_ids.includes(user.id) ? (
                            <FiUserCheck size={16} />
                          ) : (
                            <FiUser size={16} />
                          )}
                        </div>
                        <div className="user-info">
                          <span className="user-name">{user.name}</span>
                          <span className="user-email">{user.email}</span>
                          <span className={`user-role ${user.role}`}>{user.role}</span>
                        </div>
                        {formData.user_ids.includes(user.id) && <FiCheck size={16} className="check-icon" />}
                      </button>
                    ))}
                    {assignmentOptions.users.length === 0 && searchTerm && (
                      <p className="no-results">No users found matching "{searchTerm}"</p>
                    )}
                  </div>
                )}
                </div>
              </div>

              {/* Apply All Assignment Types Footer */}
              <div className="overlay-footer">
                <div className="selection-summary">
                  {formData.assignment_type === 'individual' && (
                    <span className="selection-count">
                      {formData.user_ids.length} user{formData.user_ids.length !== 1 ? 's' : ''} selected
                    </span>
                  )}
                  {formData.assignment_type === 'role' && (
                    <span className="selection-count">
                      {formData.role_ids.length} role{formData.role_ids.length !== 1 ? 's' : ''} selected
                    </span>
                  )}
                  {formData.assignment_type === 'class' && (
                    <span className="selection-count">
                      {formData.class_ids.length} class{formData.class_ids.length !== 1 ? 'es' : ''} selected
                    </span>
                  )}
                  {formData.assignment_type === 'section' && (
                    <span className="selection-count">
                      {formData.section_ids.length} section{formData.section_ids.length !== 1 ? 's' : ''} selected
                    </span>
                  )}
                </div>
                <div className="overlay-actions">
                  <button 
                    className="btn-cancel"
                    onClick={() => setShowAssignmentSearch(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn-apply" 
                    onClick={() => setShowAssignmentSearch(false)}
                    type="button"
                  >
                    Add Selected
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {errors.class_ids && <span className="error-message">{errors.class_ids}</span>}
        {errors.role_ids && <span className="error-message">{errors.role_ids}</span>}
        {errors.section_ids && <span className="error-message">{errors.section_ids}</span>}
        {errors.user_ids && <span className="error-message">{errors.user_ids}</span>}
      </div>
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.start_datetime) {
      newErrors.start_datetime = 'Start date and time is required';
    }

    if (!formData.event_type) {
      newErrors.event_type = 'Event type is required';
    }

    // Validate end datetime if provided
    if (formData.end_datetime && formData.start_datetime) {
      const startDate = new Date(formData.start_datetime);
      const endDate = new Date(formData.end_datetime);
      if (endDate <= startDate) {
        newErrors.end_datetime = 'End time must be after start time';
      }
    }

    // Assignment validation based on type
    if (formData.assignment_type === 'class' && formData.class_ids.length === 0) {
      newErrors.class_ids = 'Please select at least one class';
    }

    if (formData.assignment_type === 'role' && formData.role_ids.length === 0) {
      newErrors.role_ids = 'Please select at least one role';
    }

    if (formData.assignment_type === 'section' && formData.section_ids.length === 0) {
      newErrors.section_ids = 'Please select at least one section';
    }

    if (formData.assignment_type === 'individual' && formData.user_ids.length === 0) {
      newErrors.user_ids = 'Please select at least one person';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const eventData = {
        title: formData.title,
        description: formData.description,
        start_datetime: formData.start_datetime,
        end_datetime: formData.end_datetime || formData.start_datetime,
        event_type: formData.event_type,
        assignment_type: formData.assignment_type,
        role_ids: formData.role_ids,
        class_ids: formData.class_ids,
        section_ids: formData.section_ids,
        user_ids: formData.user_ids,
        location: formData.location,
        color: formData.color,
        is_all_day: formData.is_all_day,
        institute_id: institute.institute_id,
        academic_id: selectedAcademicId
      };

      let response;
      if (isEditMode && editingEvent) {
        // Update existing event
        response = await protonCalendarAPI.updateEvent(editingEvent.id, eventData);
      } else {
        // Create new event
        response = await protonCalendarAPI.createEvent(eventData);
      }
      
      if (response.success) {
        onClose(true); // Pass true to indicate successful creation/update
      } else {
        setErrors({ submit: response.message || `Failed to ${isEditMode ? 'update' : 'create'} event` });
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} event:`, error);
      setErrors({ submit: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} event` });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      start_datetime: '',
      end_datetime: '',
      event_type: '',
      assignment_type: 'all',
      user_role_filter_id: '',
      class_id: null,
      section_ids: [],
      user_ids: [],
      location: '',
      color: '#5856D6',
      is_all_day: false
    });
    setErrors({});
    setAcademicWarning(null);
    setShowAssignmentSearch(false);
    setSearchTerm('');
    setCurrentStep(0);
    // Clear assignment cache when modal closes
    setAssignmentOptions({ classes: [], users: [], roles: [], sections: [] });
    onClose(false);
  };

  if (!isOpen) return null;

  const currentStepData = STEPS[currentStep];

  const modalContent = (
    <div className="create-event-modal-overlay" onClick={handleClose}>
      <div
        ref={modalRef}
        className={`create-event-modal stepper-modal centered`}
        style={{
          position: 'fixed',
          left: `${modalPosition.x}px`,
          top: `auto`,
          zIndex: 1000,
          width: 'calc(100vw - 40px)',
          maxWidth: '700px',
          height: 'auto',
          maxHeight: 'calc(100vh - 40px)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {modalArrowDirection !== 'none' && (
          <div className={`modal-arrow ${modalArrowDirection}`}></div>
        )}

        {/* Stepper Header */}
        <div className="modal-header stepper-header">
          <div className="header-info">
            <div className="step-indicator">
              <span className="step-icon">{currentStepData.icon}</span>
            </div>
            <div className="step-info">
              <h5>{isEditMode ? `Edit ${currentStepData.title}` : currentStepData.title}</h5>
              <p className="step-subtitle">
                {isEditMode ? 
                  currentStepData.subtitle.replace('Create', 'Update').replace('create', 'update') : 
                  currentStepData.subtitle
                }
              </p>
            </div>
          </div>
          <button className="close-btn" onClick={handleClose} type="button">
            <FiX size={20} />
          </button>
        </div>

        {/* Stepper Progress */}
        <div className="stepper-progress">
          {STEPS.map((step, index) => (
            <div 
              key={step.id} 
              className={`step-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''} ${index < currentStep && !isStepValid(index) ? 'error' : ''}`}
              role="button"
              tabIndex={0}
              aria-current={index === currentStep}
              onClick={() => goToStep(index)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToStep(index); } }}
            >
              <div className="step-circle">
                {index < currentStep ? (
                  isStepValid(index) ? <FiCheck size={14} /> : <FiX size={14} />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <div className="step-label">{step.title}</div>
            </div>
          ))}
        </div>

        {/* Academic Year Warning */}
        {academicWarning && (
          <div className="academic-warning">
            <FiInfo size={16} />
            <span>{academicWarning.message}</span>
          </div>
        )}

        {/* Step Content */}
        <div className="step-content">
          {/* Step 1: Event Type Selection */}
          {currentStep === 0 && (
            <div className="step-panel event-type-step">
              <div className="step-intro">
                <p>Choose the type of event you want to create. This will determine the default settings and appearance.</p>
              </div>
              <div className="event-types-grid">
                {EVENT_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    className={`event-type-card ${formData.event_type === type.value ? 'selected' : ''}`}
                    onClick={() => handleEventTypeSelect(type.value)}
                  >
                    <div className="card-icon" style={{ backgroundColor: type.color + '20', color: type.color }}>
                      <span>{type.icon}</span>
                    </div>
                    <div className="card-content">
                      <h4 className="card-title">{type.label}</h4>
                      <p className="card-description">{type.description}</p>
                    </div>
                    <div className="card-color" style={{ backgroundColor: type.color }}></div>
                  </button>
                ))}
              </div>
              {errors.event_type && <div className="error-message">{errors.event_type}</div>}
            </div>
          )}

          {/* Step 2: Event Details */}
          {currentStep === 1 && (
            <div className="step-panel event-details-step">
              <div className="form-section">
                {/* Title */}
                <div className="form-group">
                  <label className="form-label required">Event Title</label>
                  <input
                    type="text"
                    className={`form-input ${errors.title ? 'error' : ''}`}
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter event title..."
                    autoFocus
                  />
                  {errors.title && <div className="error-message">{errors.title}</div>}
                </div>

                {/* Description */}
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Add a description (optional)..."
                    rows={3}
                  />
                </div>

                {/* All Day Toggle */}
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_all_day}
                      onChange={(e) => {
                        const isAllDay = e.target.checked;
                        setFormData(prev => {
                          const updatedData = { ...prev, is_all_day: isAllDay };
                          
                          // Update end_datetime based on all-day status
                          if (prev.start_datetime) {
                            const startDate = prev.start_datetime.split('T')[0];
                            
                            if (isAllDay) {
                              updatedData.end_datetime = `${startDate}T23:59`;
                            } else {
                              // Set to 1 hour after start time
                              const startTime = prev.start_datetime.split('T')[1] || '00:00';
                              const [hours, minutes] = startTime.split(':').map(Number);
                              const endHours = Math.min(hours + 1, 23);
                              const endTime = `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                              updatedData.end_datetime = `${startDate}T${endTime}`;
                            }
                          }
                          
                          return updatedData;
                        });
                      }}
                    />
                    <div className="checkbox-custom"></div>
                    All Day Event
                  </label>
                </div>

                {/* Date and Time */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">
                      <FiCalendar size={16} />
                      {formData.is_all_day ? 'Start Date' : 'Start Date & Time'}
                    </label>
                    <input
                      type={formData.is_all_day ? 'date' : 'datetime-local'}
                      className={`form-input ${errors.start_datetime ? 'error' : ''}`}
                      value={formData.is_all_day 
                        ? formData.start_datetime.split('T')[0] 
                        : formData.start_datetime
                      }
                      onChange={(e) => {
                        if (formData.is_all_day) {
                          const newStartDateTime = e.target.value + 'T00:00';
                          handleInputChange('start_datetime', newStartDateTime);
                        } else {
                          handleInputChange('start_datetime', e.target.value);
                        }
                      }}
                    />
                    {errors.start_datetime && <div className="error-message">{errors.start_datetime}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {formData.is_all_day ? 'End Date' : 'End Date & Time'}
                    </label>
                    <input
                      type={formData.is_all_day ? 'date' : 'datetime-local'}
                      className="form-input"
                      value={formData.is_all_day 
                        ? (formData.end_datetime ? formData.end_datetime.split('T')[0] : '')
                        : formData.end_datetime
                      }
                      onChange={(e) => {
                        if (formData.is_all_day) {
                          handleInputChange('end_datetime', e.target.value ? e.target.value + 'T23:59' : '');
                        } else {
                          handleInputChange('end_datetime', e.target.value);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="form-group">
                  <label className="form-label">
                    <FiMapPin size={16} />
                    Location
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Add location (optional)..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Assignment */}
          {currentStep === 2 && (
            <div className="step-panel assignment-step">
              {renderAssignmentStep()}
            </div>
          )}
        </div>

        {/* Step Navigation */}
        <div className="step-navigation">
          <div className="nav-left">
            {currentStep > 0 && (
              <button
                type="button"
                className="nav-btn btn-secondary"
                onClick={prevStep}
              >
                <FiChevronLeft size={16} />
                Back
              </button>
            )}
          </div>
          
          <div className="nav-right">
            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                className="nav-btn btn-primary"
                onClick={nextStep}
                disabled={!isCurrentStepValid()}
              >
                Next
                <FiChevronRight size={16} />
              </button>
            ) : (
              <button
                type="submit"
                className={`nav-btn btn-primary ${loading ? 'loading' : ''}`}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 
                  (isEditMode ? 'Updating...' : 'Creating...') : 
                  (isEditMode ? 'Update Event' : 'Create Event')
                }
                <FiSave size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CreateEventModal;
